import { IAttribute, IViolation } from '../../base/annotation';
import { Metamodel } from 'app/model/base/model';
import { CopySource } from './copysource';
import { Office365Dataset, FilterRequireMode } from '../../dataset/office365.dataset';
import { adfModelTypeMap } from 'app/registries/consts/adf-model-type-map-const';
import { office365UserScopes } from 'app/registries/consts/metadata-consts';
import { sanitizeAndFormatString, stringIsNullOrEmpty } from 'app/shared/utils/string.util';

import { CopyActivity } from '../copy.activity';
import { valueAndExpressionValueBothEmpty } from 'app/shared/utils/parameter.util';
import { NamedArray } from 'app/model/base/namedarray';
import { Column } from 'app/model/dataset/column';
import { arrayIsNullOrEmpty } from 'app/registries/utls/util';
import { ValueAdapter } from 'app/model/adapters/value.adapter';

@Metamodel.model({
    name: adfModelTypeMap.office365.copySourceType,
    display: [],
    tags: { datasetModel: Metamodel.modelFor(Office365Dataset) }
})
export class Office365CopySource extends CopySource {
    public static readonly ALLOWEDGROUPS: IAttribute;
    public static readonly USERSCOPEFILTERURI: IAttribute;
    public static readonly DATEFILTERCOLUMN: IAttribute;
    public static readonly STARTTIME: IAttribute;
    public static readonly ENDTIME: IAttribute;
    public static readonly USERSCOPE: IAttribute;
    public static readonly OUTPUT_COLUMNS: IAttribute;

    @Metamodel.attribute({
        association: 'ALLOWEDGROUPS',
        accepts: ['Array'],
        templatable: true
    })
    public allowedGroups: string[];

    @Metamodel.attribute({
        association: 'USERSCOPEFILTERURI',
        templatable: true
    })
    public userScopeFilterUri: string;

    @Metamodel.attribute({
        association: 'DATEFILTERCOLUMN',
        constraint: {
            validator: Office365CopySource.validatedateFilterColumn
        },
        templatable: true
    })
    public dateFilterColumn: string;

    @Metamodel.attribute({
        association: 'STARTTIME',
        constraint: {
            validator: Office365CopySource.validatedateStartTime
        },
        templatable: true
    })
    public startTime: string;

    @Metamodel.attribute({
        association: 'ENDTIME',
        constraint: {
            validator: Office365CopySource.validatedateEndTime
        },
        templatable: true
    })
    public endTime: string;

    public filterRequireMode: FilterRequireMode = FilterRequireMode.none;

    @Metamodel.attribute({
        association: 'USERSCOPE',
        templatable: true
    })
    public userScope: string = office365UserScopes.all;

    @Metamodel.attribute({
        association: 'OUTPUT_COLUMNS',
        constraint: {
            validator: Office365CopySource.validateColumns
        },
        templatable: true
    })
    public outputColumns: NamedArray<Column>;

    public static validatedateFilterColumn(entity: Office365CopySource): IViolation[] | undefined {
        const dataset = (entity.owner as CopyActivity).sourceDataset as Office365Dataset;
        const tableName = dataset && dataset.tableName;
        if (entity.filterRequireMode === FilterRequireMode.all && valueAndExpressionValueBothEmpty(entity, Office365CopySource.DATEFILTERCOLUMN)) {
            return [{
                context: entity.dateFilterColumn,
                message: () => sanitizeAndFormatString(stringResources.Office365RequireDateFilterColumnMessageWithTable, tableName, __IS_FABRIC_DPE__ ? stringResources.SourceInSmall : dataset.name)
            }];
        }
        return undefined;
    }

    public static validatedateStartTime(entity: Office365CopySource): IViolation[] | undefined {
        const dataset = (entity.owner as CopyActivity).sourceDataset as Office365Dataset;
        const tableName = dataset && dataset.tableName;
        if (entity.filterRequireMode > 0 && valueAndExpressionValueBothEmpty(entity, Office365CopySource.STARTTIME)) {
            return [{
                context: entity.startTime,
                message: () => sanitizeAndFormatString(stringResources.Office365RequireStartTimeMessageWithTable, tableName, __IS_FABRIC_DPE__ ? stringResources.SourceInSmall : dataset.name)
            }];
        }
        return undefined;
    }

    public static validatedateEndTime(entity: Office365CopySource): IViolation[] | undefined {
        const dataset = (entity.owner as CopyActivity).sourceDataset as Office365Dataset;
        const tableName = dataset && dataset.tableName;
        if (entity.filterRequireMode > 0 && valueAndExpressionValueBothEmpty(entity, Office365CopySource.ENDTIME)) {
            return [{
                context: entity.endTime,
                message: () => sanitizeAndFormatString(stringResources.Office365RequireEndTimeMessageWithTable, tableName, __IS_FABRIC_DPE__ ? stringResources.SourceInSmall : dataset.name)
            }];
        }
        return undefined;
    }

    public static validateColumns(entity: Office365CopySource): IViolation[] | undefined {
        if (__IS_FABRIC_DPE__ && !ValueAdapter.toResource(entity, Office365CopySource.OUTPUT_COLUMNS)) {
            return undefined;
        }
        // we want to validate outputColumns if entity still uses it
        const isOuputColEmpty = arrayIsNullOrEmpty(entity?.outputColumns);
        const dataset = (entity.owner as CopyActivity).sourceDataset as Office365Dataset;
        if (isOuputColEmpty && dataset.isV2) {
            return [{
                context: entity.outputColumns,
                message: () => stringResources.Office365RequireColumnsMessage
            }];
        }
        if (entity.outputColumns?.length > 0) {
            const nameSet = new Set<string>();
            const violations: IViolation[] = [];
            entity.outputColumns.forEach((column, index) => {
                if (stringIsNullOrEmpty(column.name)) {
                    violations.push({
                        context: {
                            index
                        },
                        message: () => stringResources.ColumnNameEmptyMessage
                    });
                } else if (nameSet.has(column.name)) {
                    violations.push({
                        context: {
                            index
                        },
                        message: () => stringResources.ColumnNameDuplicatedMessage
                    });
                } else {
                    nameSet.add(column.name);
                }
            });
            return violations;
        }
        return undefined;
    }
}
